import {
  Badge,
  Button,
  DrawerBackdrop,
  DrawerShell,
  DrawerShellBody,
  DrawerShellHeader,
  DrawerShellHeaderMain,
  DrawerShellIcon,
  DrawerShellTitle,
  Spinner,
  useBreakpoint,
} from '@zrsystem/ui';
import { Suspense, type CSSProperties } from 'react';

import { FormDrawerFooterSlotProvider } from '@/app/surfaces/form-drawer-footer-slot.js';
import { useFormDrawerContext } from '@/app/surfaces/form-drawer-provider.js';
import {
  formDrawerRegistry,
  getFormDrawerBadge,
  getFormDrawerIcon,
  getFormDrawerTitle,
} from '@/app/surfaces/form-drawer-registry.js';

export function FormDrawerHost() {
  const { stack, closeFormDrawer } = useFormDrawerContext();
  const breakpoint = useBreakpoint();
  const isPhone = breakpoint === 'phone';

  if (stack.length === 0) {
    return null;
  }

  return (
    <DrawerBackdrop
      onClick={() => {
        closeFormDrawer();
      }}
    >
      {stack.map((instance, index) => {
        const entry = formDrawerRegistry[instance.key];

        if (!entry) {
          return null;
        }

        const Content = entry.component;
        const rightOffset = isPhone ? 0 : getDrawerRightOffset(stack.length, index);
        const drawerWidth = isPhone ? null : mapDrawerWidth(entry.size);
        const isTop = index === stack.length - 1;
        const drawerIcon = getFormDrawerIcon(entry, instance.params);
        const drawerBadge = getFormDrawerBadge(entry, instance.params);

        return (
          <DrawerShell
            key={instance.id}
            style={buildDrawerStyle(rightOffset, index, isTop, drawerWidth, isPhone)}
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <FormDrawerFooterSlotProvider>
              <DrawerShellHeader>
                <DrawerShellHeaderMain>
                  {drawerIcon ? <DrawerShellIcon>{drawerIcon}</DrawerShellIcon> : null}
                  <DrawerShellTitle>{getFormDrawerTitle(entry, instance.params)}</DrawerShellTitle>
                  {drawerBadge ? <Badge variant="neutral">{drawerBadge}</Badge> : null}
                </DrawerShellHeaderMain>
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  aria-label="Fechar"
                  onClick={() => {
                    closeFormDrawer(instance.id);
                  }}
                >
                  Fechar
                </Button>
              </DrawerShellHeader>
              <DrawerShellBody>
                <Suspense fallback={<Spinner aria-label="Carregando formulário" />}>
                  <Content {...instance.params} />
                </Suspense>
              </DrawerShellBody>
            </FormDrawerFooterSlotProvider>
          </DrawerShell>
        );
      })}
    </DrawerBackdrop>
  );
}

function getDrawerRightOffset(stackSize: number, stackIndex: number): number {
  const depthFromTop = stackSize - 1 - stackIndex;
  return depthFromTop * 56;
}

function mapDrawerWidth(size: 'sm' | 'md' | 'lg' | 'xl' | undefined): string | null {
  switch (size) {
    case undefined:
      return null;
    case 'sm':
      return '420px';
    case 'md':
      return '480px';
    case 'lg':
      return '640px';
    case 'xl':
      return '760px';
  }
}

function buildDrawerStyle(
  rightOffset: number,
  stackIndex: number,
  isTop: boolean,
  drawerWidth: string | null,
  isPhone: boolean,
): CSSProperties {
  const style: CSSProperties & Record<string, string | number> = {
    zIndex: `calc(var(--z-drawer) + ${stackIndex})`,
    opacity: isTop ? 1 : 0.96,
  };

  if (!isPhone) {
    style.position = 'absolute';
    style.right = rightOffset;
    style.top = 0;
    style.bottom = 0;
  }

  if (drawerWidth) {
    style['--drawer-w'] = drawerWidth;
  }

  return style;
}
